<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Button,
		Alert,
		Modal,
		Container,
		Text,
		MenuItem,
		MainMenu,
		CardGrid,
		IconButton,
		ListNumberCard,
		Icon,
		CircleAvatar,
		toast,
	} from '@Shelly/shelly-design-system';

	import PreviewBox from 'src/components/PreviewBox.svelte';
	import { onStartDrag, onEndDrag } from 'src/components/Lists/list-helpers';
	import {
		appendMissingMenuOrderIndices,
		menuItems,
		menuItemsNewLayout,
		partitionMainMenuOrderIndices,
		parseHomeDataMenuOrderJson,
		MAIN_MENU_ORDER_INDEX_ADD_DEVICE,
	} from 'src/helpers/main_menu';

	// New-layout assistant footer-lock lives in `menuItemsNewLayout` (separate
	// indexing from classic `menuItems`); it's always the last entry there.
	const NEW_LAYOUT_ASSISTANT_INDEX = menuItemsNewLayout.length - 1;
	import { save_profile } from 'src/helpers/profile_helpers';
	import { profile_data } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { profiles } from 'src/stores/features/profiles';
	import { user_data } from 'src/stores/account/user';
	import { getProfileAvatarImageSrc as getReorderModalAvatarSrc } from 'src/components/Profiles/profiles_helpers';

	interface Item {
		id: string;
		isDndShadowItem?: boolean;
		index: number;
	}

	const flipDurationMs = 0;
	let showReorderMenuModal = false;
	let dragDisabled = true;
	let extractedItem: Item | null = null;
	$: menuOrder = (() => {
		const order = parseHomeDataMenuOrderJson($profile_data?.menuOrder);
		// Classic rail: ensure every item (settings, assistant, add-device, ...) is
		// present so it shows up in the reorder list even if persisted order is older.
		return appendMissingMenuOrderIndices(order, menuItems);
	})();
	$: currentOrder = [...menuOrder];
	$: customItemsAtEnd = $showNewLayout
		? [NEW_LAYOUT_ASSISTANT_INDEX]
		: [MAIN_MENU_ORDER_INDEX_ADD_DEVICE];

	let items: Item[];
	$: items = menuOrder
		.map((index) => ({
			id: menuItems[index]?.href ?? String(index),
			index,
		}))
		.filter((item) => !customItemsAtEnd.includes(item.index));

	let dndSettings: any;
	$: dndSettings = {
		items,
		flipDurationMs,
		dragDisabled,
		type: 'dashListRender',
		transformDraggedElement,
	};

	const updateHomeData = (newOrder: number[]) => {
		const newOrderStringify = JSON.stringify(newOrder);

		save_profile('menuOrder', newOrderStringify);
		profile_data.set({
			...$profile_data,
			menuOrder: newOrderStringify,
		});
	};

	const transformDraggedElement = (draggedEl: HTMLElement) => {
		const child = draggedEl.firstChild as HTMLElement;
		// Focus dragged element and hide number
		child.setAttribute('tabIndex', '-1');
		child.classList.add('on-drag');
		child.focus();
	};

	const handleStartDrag = (item?: Item) => {
		if (customItemsAtEnd.includes(item.index) && items[0].index !== item.index) {
			extractedItem = items[0];
			items = items.slice(1);
		} else {
			extractedItem = null;
		}
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		if (extractedItem) items.unshift(extractedItem);
		extractedItem = null;
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		items = [...e.detail.items];

		const newOrder = items.map((obj) => obj.index);
		for (const id of customItemsAtEnd) {
			if (!newOrder.includes(id)) newOrder.push(id);
		}

		if (extractedItem) newOrder.unshift(extractedItem.index);

		currentOrder = newOrder;
		handleEndDrag();
	};

	const onOpenReorderMenuModal = () => {
		const order = [...menuOrder];
		if ($showNewLayout) {
			const assistantIdx = order.indexOf(NEW_LAYOUT_ASSISTANT_INDEX);
			if (assistantIdx >= 0 && assistantIdx !== order.length - 1) {
				order.splice(assistantIdx, 1);
				order.push(NEW_LAYOUT_ASSISTANT_INDEX);
			}
		}
		currentOrder = order;
		items = order
			.map((index) => ({
				id: menuItems[index]?.href ?? String(index),
				index,
			}))
			.filter((item) => !customItemsAtEnd.includes(item.index));
		extractedItem = null;
		showReorderMenuModal = true;
	};

	const onCloseReorderMenuModal = () => {
		showReorderMenuModal = false;
	};

	const onSaveReorderMenu = () => {
		toast.open({
			type: 'success',
			title: $format('toast_menu_order_saved', {
				default: 'Menu order saved successfully.',
			}),
		});

		//Add Old layout add device button. If we don't add it won't render in menu items list
		currentOrder = [...currentOrder];
		onCloseReorderMenuModal();
		updateHomeData(currentOrder);
	};

	$: activeReorderProfile = $profiles.find((p) => p.isSelected);
	$: isPremiumReorderProfile = !!$user_data?.premium;
</script>

<Alert
	class="mb-s"
	heading={$format('alert_menu_items_app_settings_reorder', {
		default: 'You can reorder the items in the Menu so that it is best suited for you.',
	})}
/>
<Button size="m" class="width-md-auto width-sm-full" on:click={onOpenReorderMenuModal}>
	{$format('modal_button_reorder_menu', { default: 'Reorder Menu' })}
</Button>

<Modal
	title={$format('modal_button_reorder_menu', { default: 'Reorder Menu' })}
	showModal={showReorderMenuModal}
	on:close={onCloseReorderMenuModal}
>
	<svelte:fragment slot="content">
		<Alert
			class="mb-l"
			heading={$format('alert_menu_items_order_heading', {
				default: 'First item on the Menu order',
			})}
			isHeadingBold
			message={$format('alert_menu_items_order_message', {
				default:
					'Keep in mind that the first item that you set will be the page that you land on when you open the app.',
			})}
		/>

		<Container padding="0">
			<Text class="mb-s" styleType="h2">
				{$format('text_menu_items_rearangement_title', {
					default: 'Menu Items',
				})}
			</Text>

			{#if extractedItem}
				<CardGrid noPaddingBottom class="mb-s" widthUnit="%" sm={100}>
					<div class="select-none">
						<ListNumberCard size="s" cardNumber="01" editMode>
							<IconButton
								aria-label={$format('button_drag_to_reorder', {
									default: 'Drag to reorder',
								})}
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								disabled
							/>
							<Icon class="mr-s" icon={menuItems[extractedItem.index].icon} size="s" />
							<Text styleType="controls-small">
								{$format(menuItems[extractedItem.index].label, {
									default: menuItems[extractedItem.index].labelDefault,
								})}
							</Text>
						</ListNumberCard>
					</div>
				</CardGrid>
			{/if}

			<CardGrid
				class={showNewLayout ? 'mb-s' : 'mb-m'}
				sm={100}
				gridGap="s"
				widthUnit="%"
				editMode={true}
				{dndSettings}
				on:consider={handleReorder}
				on:finalize={handleReorderEnd}
				modalContent
				noPaddingBottom
			>
				{#each items as item, i (item)}
					{@const menuItem = $showNewLayout
						? menuItemsNewLayout[item.index]
						: menuItems[item.index]}
					{#if menuItem}
						<div class:dnd-placeholder={item?.isDndShadowItem}>
							<ListNumberCard
								cardNumber={`${i + 1 + (extractedItem ? 1 : 0)}`.padStart(2, '0')}
								size="s"
								editMode
							>
								<IconButton
									aria-label={$format('button_drag_to_reorder', {
										default: 'Drag to reorder',
									})}
									class="mr-s"
									icon="fas fa-up-down-left-right"
									size="s"
									variant="primary"
									on:mousedown={(e) =>
										onStartDrag(e, true, () => handleStartDrag(item))}
									on:touchstart={(e) =>
										onStartDrag(e, true, () => handleStartDrag(item))}
									on:mouseup={(e) => onEndDrag(e, true, handleEndDrag)}
									on:touchend={(e) => onEndDrag(e, true, handleEndDrag)}
									dndHandle
								/>
								<Icon class="mr-s" icon={menuItem.icon} size="s" />
								<Text styleType="controls-small">
									{$format(menuItem.label, { default: menuItem.labelDefault })}
								</Text>
							</ListNumberCard>
						</div>
					{/if}
				{/each}
				<div class={!$showNewLayout ? 'mb-s' : ''}>
					{#each customItemsAtEnd as lockedIndex}
						{@const lockedItem = $showNewLayout
							? menuItemsNewLayout[lockedIndex]
							: menuItems[lockedIndex]}
						{@const isNewLayoutAssistantRow =
							$showNewLayout && lockedIndex === NEW_LAYOUT_ASSISTANT_INDEX}
						{@const lockedRowOrdinal =
							items.length +
							(extractedItem ? 1 : 0) +
							customItemsAtEnd.indexOf(lockedIndex) +
							1}
						{@const lockedRowCardNumber = isNewLayoutAssistantRow
							? undefined
							: `${lockedRowOrdinal}`.padStart(2, '0')}
						{@const lockedRowListIcon = isNewLayoutAssistantRow ? 'fa-solid fa-map-pin' : ''}
						{#if lockedItem}
							<div class={`select-none ${!$showNewLayout ? 'mb-s' : ''}`}>
								<ListNumberCard
									size="s"
									cardNumber={lockedRowCardNumber}
									icon={lockedRowListIcon}
									editMode
								>
									<IconButton
										aria-label={$format('button_drag_to_reorder', {
											default: 'Drag to reorder',
										})}
										class="mr-s"
										icon="fas fa-up-down-left-right"
										size="s"
										variant="primary"
										disabled
									/>
									<Icon class="mr-s" icon={lockedItem.icon} size="s" />
									<Text styleType="controls-small">
										{$format(lockedItem.label, { default: lockedItem.labelDefault })}
									</Text>
								</ListNumberCard>
							</div>
						{/if}
					{/each}
				</div>
			</CardGrid>

			{#if $showNewLayout}
				<div style="margin-left: -3px;">
					<div class="select-none">
						<ListNumberCard size="s" icon="fa-solid fa-map-pin" editMode class="mb-m">
							<IconButton
								aria-label={$format('button_drag_to_reorder', {
									default: 'Drag to reorder',
								})}
								class="mr-s"
								icon="fas fa-up-down-left-right"
								size="s"
								variant="primary"
								disabled
							/>
							<div class="menu-reorder-profile-row">
								{#key activeReorderProfile?.id || activeReorderProfile?.acc_avatar}
									<CircleAvatar
										class="mr-s"
										size="s"
										text={activeReorderProfile?.acc_avatar?.text}
										image={getReorderModalAvatarSrc(activeReorderProfile)}
										icon={!activeReorderProfile?.acc_avatar?.backgroundColor &&
											'fak fa-solid-shelly'}
										backgroundColor={activeReorderProfile?.acc_avatar
											?.backgroundColor}
										disabled
										avatarOptions={{
											type: isPremiumReorderProfile ? 'premium' : null,
											badgeProps: { show: isPremiumReorderProfile },
										}}
									/>
								{/key}
								<Text styleType="controls-small">
									{$format('label_profile', { default: 'Profile' })}
								</Text>
							</div>
						</ListNumberCard>
					</div>
				</div>
			{/if}

			<Text class="mb-s" styleType="h2">
				{$format('text_modal_menu_items_order_preview', {
					default: 'Preview',
				})}
			</Text>
			<PreviewBox>
				<MainMenu isMobile useNewLayout={$showNewLayout}>
					{#if $showNewLayout}
						{#each currentOrder as itemIndex}
							{@const item = menuItemsNewLayout[itemIndex]}
							{#if item}
								<MenuItem
									isMobile
									useNewLayout={$showNewLayout}
									icon={item.icon}
									class={item.custom ? 'menu-item-custom-icon' : ''}
									label=""
								/>
							{/if}
						{/each}

						<div
							class="menu-reorder-preview-profile"
							aria-label={$format('label_profile', { default: 'Profile' })}
						>
							{#key activeReorderProfile?.id || activeReorderProfile?.acc_avatar}
								<CircleAvatar
									size="m"
									text={activeReorderProfile?.acc_avatar?.text}
									image={getReorderModalAvatarSrc(activeReorderProfile)}
									icon={!activeReorderProfile?.acc_avatar?.backgroundColor &&
										'fak fa-solid-shelly'}
									backgroundColor={activeReorderProfile?.acc_avatar?.backgroundColor}
									disabled
									avatarOptions={{
										type: isPremiumReorderProfile ? 'premium' : null,
										badgeProps: { show: isPremiumReorderProfile },
									}}
								/>
							{/key}
						</div>
					{:else}
						{@const docked = partitionMainMenuOrderIndices(currentOrder, menuItems)}
						<div class="menu-order-preview-dock">
							<div class="menu-order-preview-dock__primary">
								{#each docked.primary as itemIndex}
									{@const item = menuItems[itemIndex]}
									{#if item}
										<MenuItem
											isMobile
											useNewLayout={false}
											icon={item.icon}
											class={item.custom ? 'menu-item-custom-icon' : ''}
											label={$format(item.label, { default: item.labelDefault })}
										/>
									{/if}
								{/each}
							</div>
							{#if docked.footer.length}
								<div class="menu-order-preview-dock__footer">
									{#each docked.footer as itemIndex}
										{@const item = menuItems[itemIndex]}
										{#if item}
											<MenuItem
												isMobile
												useNewLayout={false}
												icon={item.icon}
												class={item.custom ? 'menu-item-custom-icon' : ''}
												label={$format(item.label, {
													default: item.labelDefault,
												})}
											/>
										{/if}
									{/each}
								</div>
							{/if}
						</div>
					{/if}
				</MainMenu>
			</PreviewBox>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{ sm: 'm' }}
			on:click={onCloseReorderMenuModal}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{ sm: 'm' }}
			on:click={onSaveReorderMenu}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>

<style>
	.menu-reorder-profile-row {
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 0;
	}

	.menu-reorder-preview-profile {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 8px;
		border-radius: var(--radius-small);
		box-sizing: border-box;
	}

	.menu-order-preview-dock {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: center;
		width: 100%;
		min-width: 0;
		gap: var(--spacing-m);
		padding-inline: var(--spacing-m);
		box-sizing: border-box;
	}

	.menu-order-preview-dock__primary,
	.menu-order-preview-dock__footer {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-m);
		flex: 0 0 auto;
		min-width: 0;
	}

	.menu-order-preview-dock__footer {
		margin-left: 0;
		padding-left: 0;
		border-left: none;
	}

	.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
	.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
		flex: 1 1 0 !important;
		min-width: 0 !important;
		width: auto !important;
		max-width: 96px !important;
		box-sizing: border-box !important;
		overflow: hidden;
	}

	.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout) .menu-item-text) {
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	@media screen and (max-width: 1123px) {
		.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
		.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
			max-width: 78px !important;
		}
	}

	@media screen and (max-width: 639px) {
		.menu-order-preview-dock {
			gap: var(--spacing-s);
			padding-inline: var(--spacing-s);
		}

		.menu-order-preview-dock__primary,
		.menu-order-preview-dock__footer {
			gap: var(--spacing-s);
		}

		.menu-order-preview-dock :global(.menu-item.mobile:not(.new-layout)),
		.menu-order-preview-dock :global(.menu-item.mobile:not(.desktop):not(.new-layout)) {
			max-width: 64px !important;
		}
	}
</style>
